import { ActionIcon, Checkbox, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { useFeatureFlagsContext } from '@/context/FeatureFlagsContext';
import {
  FEATURE_FLAG_DEFINITIONS,
  type FeatureFlagDefinition,
} from '@/utils/featureFlagDefinitions';
import { experimentTogglePath } from '@/utils/experimentQueryParams';
import { FeatureFlag } from '@/utils/featureFlags';
import ShareIcon from '@/assets/icons/content/share.svg';

type ExperimentalFeaturesModalProps = {
  opened: boolean;
  onClose: () => void;
};

const shareableLink = (flag: FeatureFlag, enabled: boolean) =>
  `${window.location.origin}${experimentTogglePath(flag, enabled)}`;

const LinkCopy = ({ hint, url, color }: { hint: string; url: string; color: 'green' | 'gray' }) => {
  const clipboard = useClipboard({ timeout: 1000 });

  return (
    <Tooltip label={clipboard.copied ? 'Copied' : hint}>
      <ActionIcon
        size="sm"
        variant="subtle"
        color={color}
        aria-label={hint}
        onClick={() => clipboard.copy(url)}
      >
        <ShareIcon width={14} height={14} />
      </ActionIcon>
    </Tooltip>
  );
};

const FeatureFlagRow = ({
  def,
  checked,
  onChange,
}: {
  def: FeatureFlagDefinition;
  checked: boolean;
  onChange: (enabled: boolean) => void;
}) => (
  <Group
    justify="space-between"
    align="center"
    wrap="nowrap"
    gap={0}
    data-testid={`experiment-${def.key}`}
  >
    <Checkbox
      flex={1}
      label={
        <Stack gap={0}>
          <Text fw={600}>{def.label}</Text>
          <Text size="xs" c="dimmed">
            {def.description}
          </Text>
        </Stack>
      }
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
    <Group gap={0} wrap="nowrap">
      <Text size="xs" c="dimmed">
        Share:
      </Text>
      <LinkCopy
        hint={`Copy Link: Enable ${def.label}`}
        url={shareableLink(def.key, true)}
        color="green"
      />
      <LinkCopy
        hint={`Copy Link: Disable ${def.label}`}
        url={shareableLink(def.key, false)}
        color="gray"
      />
    </Group>
  </Group>
);

const ExperimentalFeaturesModal = ({ opened, onClose }: ExperimentalFeaturesModalProps) => {
  const { flags, setFlag } = useFeatureFlagsContext();

  return (
    <Modal opened={opened} onClose={onClose} title="Experimental Features" size="lg">
      <Text mb="md" size="sm" c="dimmed">
        <p>
          This panel controls experimental Mercury features. Once selected, a feature will be
          enabled in this browser until deselected.
        </p>
        <p>Share links may be sent to customers for previewing a specific feature.</p>
      </Text>

      <Stack gap="sm">
        {FEATURE_FLAG_DEFINITIONS.map((def) => (
          <FeatureFlagRow
            key={def.key}
            def={def}
            checked={!!flags[def.key]}
            onChange={(enabled) => setFlag(def.key, enabled)}
          />
        ))}
      </Stack>
    </Modal>
  );
};

export default ExperimentalFeaturesModal;
